/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {GlobalScenario} from '../../../models/global-scenario-dto.model';
import {ComparePropVolRptRequest} from '../../../models/reports/compare-prop-vol-rpt-request.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {ReportService} from '../../../services/report.service';

@Component({
    selector: 'app-compare-prop-vol-rpt',
    templateUrl: './compare-prop-vol-rpt.component.html',
    styleUrl: './compare-prop-vol-rpt.component.scss',
    standalone: false
})
export class ComparePropVolRptComponent implements OnInit {
  // Report types
  WORKING_VS_COMPARE = 1;
  YR_OVER_YR = 2;

  // Geography flags
  SUBREGION_FLAG = 1;
  MKTAREA_FLAG = 2;
  PLNGMKT_FLAG = 3;

  SUBREGION = 'SUBREGION';
  MKTAREA = 'MKTAREA';
  PLNGMKT = 'PLNGMKT';
  BRAND = 'BRAND';
  PRGMCODE = 'PRGMCODE';

  showSpinner: boolean;
  areScenariosSelected: boolean;
  isGlobal: boolean;

  selectedWorkingScenario: VehicleScenario;
  selectedCompareScenario: VehicleScenario;
  selectedWorkingGlobalScenario: GlobalScenario;
  selectedCompareGlobalScenario: GlobalScenario;
  selectedMemberScenarios: VehicleScenario[];
  selectedGeography: number;
  selectedReportType: number;

  allFilterOptions: any[] = [];
  filteredData: any[] = [];
  geographyOptions: SelectableOptions = new SelectableOptions();
  brandOptions: SelectableOptions = new SelectableOptions();
  prgmCodeOptions: SelectableOptions = new SelectableOptions();

  yearRange: number[];
  startYear: number;
  endYear: number;

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              public reportService: ReportService,
              public stateService: StateService,
              public dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.isGlobal = false;
    this.selectedReportType = this.WORKING_VS_COMPARE;
    this.selectedGeography = this.PLNGMKT_FLAG;

    // If there is already a selected scenario within the state, then use that selected scenario
    if (this.stateService.isVehicleScenarioSelected) {
      this.selectedWorkingScenario = this.stateService.selectedScenario;
      this.selectedCompareScenario = this.stateService.selectedCompareScenario;
    }
    if (this.selectedWorkingScenario) {
      this.getFilterData();
    }

    this.getYearRange();
    this.setScenariosSelected();
  }

  /**
   * Run the report and export to Excel
   */
  onRunReport(): void {
    const request = new ComparePropVolRptRequest();
    let compareScenId = 0;
    let compareScenName = '';
    let comparePropScenId = 0;
    let comparePropScenName = '';
    if (this.selectedReportType === this.WORKING_VS_COMPARE) {
      compareScenId = this.isGlobal ? this.selectedCompareGlobalScenario.id : this.selectedCompareScenario.vehScenId;
      compareScenName = this.isGlobal ? this.selectedCompareGlobalScenario.globalScenarioName : this.selectedCompareScenario.vehScenName;
      comparePropScenId = this.isGlobal ? 0 : this.selectedCompareScenario.propulsionScenario.id;
      comparePropScenName = this.isGlobal ? null : this.selectedCompareScenario.propulsionScenario.propulsionScenarioName;
    }
    request.geographyFlag = this.selectedGeography;
    request.workingScenId = this.isGlobal ? this.selectedWorkingGlobalScenario.id : this.selectedWorkingScenario.vehScenId;
    request.workingScenName = this.isGlobal ? this.selectedWorkingGlobalScenario.globalScenarioName : this.selectedWorkingScenario.vehScenName;
    request.compareScenId = compareScenId;
    request.compareScenName = compareScenName;
    request.workingPropScenId = this.isGlobal ? null : this.selectedWorkingScenario.propulsionScenario.id;
    request.workingPropScenName = this.isGlobal ? null : this.selectedWorkingScenario.propulsionScenario.propulsionScenarioName;
    request.comparePropScenId = comparePropScenId;
    request.comparePropScenName = comparePropScenName;
    request.startYear = this.startYear;
    request.endYear = this.endYear;
    request.geography = this.geographyOptions.selectedOptions.length === 0 ? 'All' : this.geographyOptions.selectedOptions.join(', ');
    request.brands = this.brandOptions.selectedOptions.length === 0 ? 'All' : this.brandOptions.selectedOptions.join(', ');
    request.prgmCodes = this.prgmCodeOptions.selectedOptions.length === 0 ? 'All' : this.prgmCodeOptions.selectedOptions.join(', ');
    request.rptType = this.selectedReportType;
    request.isGlobal = this.isGlobal;

    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const workingScenarioId = this.isGlobal ?
      this.selectedWorkingGlobalScenario.id : this.selectedWorkingScenario.vehScenId;
    const scenarioString = compareScenId !== 0 ?
      workingScenarioId + "-" + compareScenId : workingScenarioId;

    const fileName = 'ComparePropVol_' + scenarioString + "_" + currDate;
    this.showSpinner = true;
    this.reportService.getComparePropVolReport(request).subscribe({
      next: (response: any) => {
        this.excelService.exportToExcelFromServer(response, fileName);
      },
      error: (error: any) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
      }
    });
  }

  /**
   * Event handler when user wants to select a vehicle scenario
   */
  openVehSelectComponent(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedWorkingScenario = res.selectedScenarios[0];
        this.selectedCompareScenario = this.selectedWorkingScenario;
        this.setScenariosSelected();
        this.getFilterData();
      }
    });
  }

  /**
   * Opens the SelectCompareScenario pop up component
   */
  selectCompareScenario(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.selectedWorkingScenario.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedCompareScenario = res.selectedScenarios[0];
        this.setScenariosSelected();
      }
    });
  }

  /**
   * Event handler for when a working global scenario is selected
   * @param data the data from the event
   */
  onGlobalScenarioSelect(data: any): void {
    this.selectedWorkingGlobalScenario = data.globalScenario;
    this.selectedMemberScenarios = data.memberScenarios;
    this.setScenariosSelected();
    this.getFilterData();
  }


  /**
   * Event handler for when a compare global scenario is selected
   * @param data the data from the event
   */
  onCompareGlobalScenarioSelect(data: any): void {
    this.selectedCompareGlobalScenario = data.globalScenario;
    this.setScenariosSelected();
  }

  /**
   * Sets the boolean for whether both working and compare scenarios are selected. Used for disabling the Run Report button
   */
  setScenariosSelected(): void {
    if (this.isGlobal) {
      this.areScenariosSelected = this.selectedWorkingGlobalScenario !== undefined && this.selectedWorkingGlobalScenario !== null
        && this.selectedCompareGlobalScenario !== undefined && this.selectedCompareGlobalScenario !== null;

      // Year over Year doesn't need a compare scenario
      if (this.selectedReportType === this.YR_OVER_YR) {
        this.areScenariosSelected = this.selectedWorkingGlobalScenario !== undefined && this.selectedWorkingGlobalScenario !== null;
      }
    } else {
      this.areScenariosSelected = this.selectedWorkingScenario !== undefined && this.selectedWorkingScenario !== null
        && this.selectedCompareScenario !== undefined && this.selectedCompareScenario !== null;
    }
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Makes a service call to fetch data to populate the filters
   */
  getFilterData(): void {
    this.showSpinner = true;
    const ids = this.isGlobal ? this.selectedWorkingGlobalScenario.id.toString() : this.selectedWorkingScenario.vehScenId.toString();
    this.reportService.getFiltersForComparePropVolReport(this.stateService.getUserGmid(), ids, this.isGlobal)
    .subscribe((data: any) => {
      this.allFilterOptions = data;
      this.filteredData = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
      this.resetSelectedFilters();
      this.initializeFilterOptions();
    });
  }

  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.allFilterOptions;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.applyFilter(this.SUBREGION, this.geographyOptions.selectedOptions);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.applyFilter(this.MKTAREA, this.geographyOptions.selectedOptions);
    } else {
      this.applyFilter(this.PLNGMKT, this.geographyOptions.selectedOptions);
    }
    this.applyFilter(this.BRAND, this.brandOptions.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.prgmCodeOptions.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      // eslint-disable-next-line
      case this.SUBREGION:
        this.brandOptions.options = this.setFilterOption(this.BRAND);
        this.brandOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTAREA:
        this.brandOptions.options = this.setFilterOption(this.BRAND);
        this.brandOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.brandOptions.options = this.setFilterOption(this.BRAND);
        this.brandOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.BRAND:
        this.prgmCodeOptions.options = this.setFilterOption(this.PRGMCODE);
        this.prgmCodeOptions.selectedOptions = [];
    }
  }

  /**
   * Initializes the filter options
   */
  initializeFilterOptions(): void {
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.SUBREGION);
      this.setCascadingFilterOptions(this.SUBREGION);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.MKTAREA);
      this.setCascadingFilterOptions(this.MKTAREA);
    } else {
      this.geographyOptions.options = this.setFilterOption(this.PLNGMKT);
      this.setCascadingFilterOptions(this.PLNGMKT);
    }
  }

  /**
   * Resets the selected filters
   */
  resetSelectedFilters(): void {
    this.geographyOptions.selectedOptions = [];
    this.brandOptions.selectedOptions = [];
    this.prgmCodeOptions.selectedOptions = [];
  }
}
